import React, { useEffect, useState, useCallback } from 'react';
import {
  TextField, Button, MenuItem, IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import './webstoreAdminServices.scss';

const categories = ['Web Development', 'SEO', 'Graphic Design', 'Hosting', 'Maintenance', 'Consultation'];

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

// Move Modal component definition outside to prevent re-creation on every render
const CustomModal = React.memo(({ open, onClose, title, children }) => {
  if (!open) return null;

  return (
    <div className="custom-modal-overlay" onClick={onClose}>
      <div className="custom-modal" onClick={(e) => e.stopPropagation()}>
        <div className="custom-modal-header">
          <h3>{title}</h3>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </div>
        <div className="custom-modal-body">{children}</div>
      </div>
    </div>
  );
});

const WebstoreAdminServices = () => {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', category: '', price: '', tags: '',
    deliveryTime: '', imageInput: '', images: [], isActive: true
  });
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/webstore-services`);
      setServices(res.data);
    } catch {
      toast.error('Failed to fetch services');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddImage = () => {
    const url = form.imageInput.trim();
    if (!url) {
      toast.warn('Please enter an image URL.');
      return;
    }
    if (form.images.includes(url)) {
      toast.warn('Image URL already added.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      images: [...prev.images, url],
      imageInput: '', // Clears the input field after adding the image
    }));
  };

  const handleRemoveImage = (url) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((img) => img !== url)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags.split(',').map(t => t.trim()),
      deliveryTime: Number(form.deliveryTime),
      isActive: Boolean(form.isActive)
    };

    try {
      if (editingServiceId) {
        await axios.put(`${API_BASE_URL}/api/webstore-services/${editingServiceId}`, payload, {
          headers: { 'x-admin-auth': 'navarrojuben' }
        });
        toast.success('Service updated');
      } else {
        await axios.post(`${API_BASE_URL}/api/webstore-services`, payload, {
          headers: { 'x-admin-auth': 'navarrojuben' }
        });
        toast.success('Service added');
      }
      fetchServices();
      resetForm();
    } catch {
      toast.error('Failed to save service');
    }
  };

  const resetForm = () => {
    setForm({
      title: '', description: '', category: '', price: '',
      tags: '', deliveryTime: '', imageInput: '', images: [], isActive: true
    });
    setEditingServiceId(null);
    setOpenFormModal(false);
  };

  const confirmServiceDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/webstore-services/${selectedServiceId}`, {
        headers: { 'x-admin-auth': 'navarrojuben' }
      });
      toast.success('Service deleted');
      fetchServices();
    } catch {
      toast.error('Failed to delete service');
    } finally {
      setOpenDeleteDialog(false);
      setSelectedServiceId(null);
    }
  };

  // Memoize the handler for opening the edit modal to prevent unnecessary re-renders
  const handleEditClick = useCallback((service) => {
    setForm({
      title: service.title ?? '',
      description: service.description ?? '',
      category: service.category ?? '',
      price: String(service.price ?? ''),
      tags: Array.isArray(service.tags) ? service.tags.join(', ') : '',
      deliveryTime: String(service.deliveryTime ?? ''),
      imageInput: '',
      images: Array.isArray(service.images) ? service.images : [],
      isActive: service.isActive !== false
    });
    setEditingServiceId(service._id);
    setOpenFormModal(true);
  }, []); // Empty dependency array means this function is created once

  // Memoize the handler for opening the delete modal
  const handleDeleteClick = useCallback((serviceId) => {
    setSelectedServiceId(serviceId);
    setOpenDeleteDialog(true);
  }, []); // Empty dependency array

  return (
    <div className="admin-services">
      <Button
        type="button"
        className="add-service-btn"
        variant="contained"
        onClick={() => {
          setForm({
            title: '', description: '', category: '', price: '',
            tags: '', deliveryTime: '', imageInput: '', images: [], isActive: true
          });
          setEditingServiceId(null);
          setOpenFormModal(true);
        }}
      >
        Add New Service
      </Button>

      {/* Add/Edit Modal */}
      <CustomModal
        open={openFormModal}
        onClose={() => setOpenFormModal(false)}
        title={editingServiceId ? 'Edit Service' : 'Add New Service'}
      >
        <form onSubmit={handleSubmit} className="service-form">
          <TextField name="title" label="Title" value={form.title} onChange={handleFormChange} required fullWidth />
          <TextField name="description" label="Description" multiline rows={3} value={form.description} onChange={handleFormChange} required fullWidth />
          <TextField name="price" label="Price" type="number" value={form.price} onChange={handleFormChange} required fullWidth />
          <TextField name="deliveryTime" label="Delivery Time (days)" type="number" value={form.deliveryTime} onChange={handleFormChange} required fullWidth />

          <div className="image-uploader">
            <TextField name="imageInput" label="Add Image URL" value={form.imageInput} onChange={handleFormChange} fullWidth />
            <Button type="button" onClick={handleAddImage} variant="outlined">Add Image</Button>
            <div className="image-preview-list">
              {form.images.map((url, idx) => (
                <div key={idx} className="image-preview-item">
                  <img src={url} alt="Preview" className="image-preview-img" />
                  <IconButton size="small" onClick={() => handleRemoveImage(url)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>

          <TextField name="category" label="Category" select value={form.category} onChange={handleFormChange} required fullWidth>
            {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
          </TextField>
          <TextField name="tags" label="Tags (comma-separated)" value={form.tags} onChange={handleFormChange} fullWidth />
          <label className="active-checkbox">
            <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleFormChange} /> Active
          </label>
          <Button type="submit" variant="contained">{editingServiceId ? 'Update' : 'Add'} Service</Button>
        </form>
      </CustomModal>

      {/* Delete Modal */}
      <CustomModal open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} title="Delete Service">
        <p>This action cannot be undone. Are you sure?</p>
        <div className="modal-actions">
          <Button onClick={confirmServiceDelete} color="error" variant="contained">Yes, Delete</Button>
          <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined">Cancel</Button>
        </div>
      </CustomModal>

      <div className="service-list">
        {services.map(service => (
          <motion.div key={service._id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="service-card">
            <h3>{service.title}</h3>
            <p>{service.description}</p>
            {/* Modified line for robust PHP currency display */}
            <small>
              {service.category} | ₱
              {new Intl.NumberFormat('en-PH', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(service.price)}
            </small>
            <div className="actions">
              <IconButton onClick={() => handleEditClick(service)}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteClick(service._id)}>
                <DeleteIcon />
              </IconButton>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default WebstoreAdminServices;